/**
 * IMPORTANT!!!
 * DO NOT EDIT THIS FILE UNLESS YOU KNOW EXACTLY WHAT YOU'RE DOING.
 */
import { ReactNode } from 'react';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  startWith,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { ChatMessage } from 'types/Chat';
import { $get, $post, Options } from 'services/api';
import { snackbarV2Error, snackbarV2Success } from 'services/snackbarV2';
import { pusher as PusherService } from 'services/pusher';
import { getChatMessage } from 'utils/getChatMessage';
import { UseFormReturn } from 'react-hook-form';
import { differenceInMinutes } from 'date-fns';

const modalHelper = <
  T1 extends {
    [key in string]: object;
  }
>() => {
  const bs = new BehaviorSubject<{
    type: keyof T1;
    data: T1[keyof T1];
  } | null>(null);
  return {
    openModal: <T extends keyof T1>(type: T, data: T1[T]) =>
      bs.next({
        type,
        data,
      }),
    $getModal: <T extends keyof T1>(type: T) =>
      bs.pipe(
        // filter((t) => !t || t?.type === type),
        map((m) => (m?.type === type ? m?.data : null) as any),
        distinctUntilChanged()
      ) as Observable<T1[T] | null>,
    closeModal: () => bs.next(null),
  };
};

/**
 * Constructor does not seem to guarantee run once when it comes to http requests
 * This is same as Angular's ngOnInit
 * Runs once when the BLoC is created
 * Use when you want to run logic on init. Never put any logic into ctor
 */
export interface IBLoCInitialisable {
  onInit?: () => void;
}

/**
 * In case there are any things that BLoC needs to clean up add this
 * interface to your bloc and you will be able to add logic
 * that you can run when BLoC gets destroyed
 */
export interface IBLoCDestroyable {
  onDestroy?: () => void;
}

/**
 * Type helper for BLoC wrapper component
 *
 * @param children  You can pass a ReactNode (as usial) or if you need you can pass in
 *                  a function that would pass in an instance of BLoC.
 *
 * @param Partial<State> Partial state that you may want to pass from main smart component
 * @
 */
export type BLoCParams<B extends BLoCBase<State>, State extends object, T = {}> = T & {
  children?: ReactNode | ((bloc: B) => ReactNode | null) | undefined;
} & Partial<State>;
export const renderBlocChild = <T extends Readonly<BLoCBase<any>>>(children: any, bloc: T) =>
  typeof children !== 'function' ? children : children && (children as any)(bloc);

/**
 * Summary.       Your BLoC components should extand from this class.
 *
 * Description.   This class stores current BLoC state in a Subject.
 *                Class contains mutators and getter helper functions for dealing with state.
 *                Instance of this class should always be frozen to avoid introducing any mutable state
 *
 * @argument initState  Pass in any initial state that you want this BLoC to inialise with.
 */
export class BLoCBase<State extends { [key in string]: any }> {
  private readonly state: BehaviorSubject<State>;
  private subs: { [key in string]: Subscription } = {};

  constructor(initState: State | null = null) {
    this.state = new BehaviorSubject<State>(initState || ({} as any));
  }

  /**
   * Adds subscription to the list of cleanups
   * Use when you need to directly sub to Observable from BLoC
   * @param sub Subscription object
   * @param key Key that you can use to cancel manual
   */
  protected addSub = (sub: Subscription, key?: string) => {
    key && this.unsub(key);
    this.subs[key ?? Object.keys(this.subs).length] = sub;
  };
  /**
   * Use to manualy unsub Sub that was added via addSub method
   * @param key Unique key that was passed using addSub
   */
  public unsub = (key?: string) => {
    if (!key) {
      Object.values(this.subs).forEach((s) => s.unsubscribe());
    } else if (key && this.subs[key]) {
      this.subs[key].unsubscribe();
    }
  };

  /**
   * Returns current state as of now.
   * Use as a quick hand to get current state within BLoC
   * This is done to simplefy. Do not abuse.
   * Use getState instead if you can.
   * @param name State var name
   * @returns Frozen Readonly version of current state for given name
   */
  protected readonly currentState = <K extends keyof State>(name: K) => this.state.value[name];

  /**
   *
   * @param name Name of the state key that you would like to access
   * @returns An Observable that provide access to the key. This will only emit on distinct change.
   *          So when state is set if this key was not changed it will not emit.
   *          Note that if data type is not object (num,str,etc) observable will not re-emit until value has changed
   *          and object will emit if ref has changed
   */
  protected readonly $getState = <K extends keyof State>(name: K) =>
    this.state.pipe(
      map((a) => a[name]),
      distinctUntilChanged()
    );

  /**
   *
   * @param mapper  a function that takes state and then allows you to create an observable from
   *                from a specific path
   * @returns Same as $getState
   */
  protected readonly $mapState = (mapper: (s: State) => any) =>
    this.state.pipe(map(mapper), distinctUntilChanged());

  /**
   *
   * @param name Key of the state to change
   * @param value New value, Must be different from prev value or it will not set. If object will freeze
   * @returns void
   */
  protected readonly setState = <K extends keyof State, Z extends State[K]>(name: K, value: Z): void => {
    this.state.value[name] !== value &&
      this.state.next(
        Object.assign({}, this.state.value, {
          [name]: value && typeof value === 'object' ? Object.freeze(value) : value, // ensure state is immutable
        })
      );
  };

  /**
   *
   * @param name Key of the state to change
   * @param mutator Takes in a function that passes previous state and expects new state to be returned
   * @returns void
   */
  protected readonly mutateState = <K extends keyof State, Z extends State[K]>(
    name: K,
    mutator: (prev: Z) => Z
  ): void => {
    const prev = this.currentState(name);
    this.state.next(
      Object.assign({}, this.state.value, {
        [name]: mutator(prev),
      })
    );
  };

  /**
   *
   * @param partialState Pass in the whole new state object (partial) and it will override any matching state
   * @returns void
   */
  protected readonly setStates = (partialState: Partial<State>): void =>
    this.state.next(Object.assign({}, this.state.value, partialState));

  protected readonly firstAndSwitchMap = <T,>($ob: Observable<T>, res: (r: T) => Observable<any>) =>
    $ob.pipe(first(), switchMap(res));
}

export class BLoCWithModalsBase<
  State extends { [key in string]: any },
  TModals extends {
    [key in string]: object;
  } = {}
> extends BLoCBase<State> {
  private readonly modalState = modalHelper<TModals>();
  public $getModal = this.modalState.$getModal;
  protected _openModal = this.modalState.openModal;
  protected _closeModal = this.modalState.closeModal;
}

type BLoCWithChatBaseConfig = {
  endpoints?: {
    saveMessageFeedback?: (messageId: ChatMessage['id']) => string;
    getChatMessages?: (id: number) => string;
    sendMessage?: (id: number) => string;
  };
};

export class BLoCWithChatBaseFormMessage {
  public message: string = '';
}

const MESSAGE_TIMEOUT_MINUTES = 15;

export class BLoCWithChatBase<State extends { [key in string]: any }> extends BLoCBase<
  State & {
    chatMessages?: ChatMessage[];
    chatId?: ChatMessage['chatId'];
    refreshDate?: Date;
  }
> {
  public $refreshDate = this.$getState('refreshDate');
  public $scrollToBottom = BLoCWithChatBaseBLoCSubject.$subject.pipe(
    filter((event) => event.type === 'scrollToBottom'),
    map((event) => event.payload)
  );
  protected messageMinutesTimeout = MESSAGE_TIMEOUT_MINUTES;
  protected chatConfig?: BLoCWithChatBaseConfig;
  protected pusher: typeof PusherService;
  public readonly messageForm: UseFormReturn<BLoCWithChatBaseFormMessage>;

  protected SUB_ID_LISTEN_CHAT_MESSAGE_EVENTS = 'listenChatMessageEvents';
  protected SUB_ID_LISTEN_CHAT_MESSAGE_ERROR_EVENTS = 'listenChatMessageErrorEvents';
  protected SUB_ID_FETCH_REPLACE_MESSAGE_PAIR = 'fetchAndReplaceUserAgentMessagesPair';
  protected SUB_ID_SEND_MESSAGE = 'sendMessageFn';

  public $aiAgentResponding = this.$getState('chatMessages').pipe(
    map((messages) => {
      const lastMessage = messages?.[0];
      return !!messages?.length && !!(lastMessage?.awaiting && lastMessage.isAiResponse);
    }),
    startWith(true),
    distinctUntilChanged()
  );

  public $disabled = combineLatest([this.$aiAgentResponding, this.$getState('chatMessages')]).pipe(
    map(([aiAgentResponding, messages]) => !messages || aiAgentResponding),
    startWith(true)
  );

  constructor(
    pusher: typeof PusherService,
    messageForm: UseFormReturn<BLoCWithChatBaseFormMessage>,
    initState: State | null = null,
    chatConfig?: BLoCWithChatBaseConfig
  ) {
    super(initState);
    this.messageForm = messageForm;
    this.chatConfig = chatConfig;
    this.pusher = pusher;
  }

  public refreshChatPage = () => this.setState('refreshDate', new Date() as State['refreshDate']);

  public resetMessageForm = () => this.messageForm.reset({ message: '' });

  public scrollToBottom = () =>
    BLoCWithChatBaseBLoCSubject.$subject.next({ type: 'scrollToBottom', payload: {} });

  get saveMessageFeedbackEndpoint() {
    return this.chatConfig?.endpoints?.saveMessageFeedback;
  }

  get getChatMessagesEndpoint() {
    return this.chatConfig?.endpoints?.getChatMessages;
  }

  get sendMessageEndpoint() {
    return this.chatConfig?.endpoints?.sendMessage;
  }

  protected setInitialChatState = (payload: { chatId: State['chatId']; messages: State['chatMessages'] }) => {
    const { chatId, messages: messages } = payload;
    const formattedMessages = this.formatChatMessages(messages);

    if (chatId) {
      this.setState('chatId', chatId);
      if (formattedMessages) {
        if (formattedMessages?.length) {
          const lastMessage = formattedMessages[0];
          const isAiResponse = lastMessage?.isAiResponse;
          const isRecent = lastMessage && this.isRecentMessage(lastMessage);

          if (isAiResponse) {
            this.setState('chatMessages', formattedMessages);
          } else if (isRecent) {
            this.setState('chatMessages', [
              {
                ...getChatMessage({ message: 'Thinking', awaiting: true, isAiResponse: true }),
                ...(lastMessage.adPersona && { adPersona: lastMessage.adPersona }),
              },
              ...formattedMessages,
            ] as State['chatMessages']);
          } else {
            this.setState('chatMessages', formattedMessages);
            snackbarV2Error({
              title: 'We could not process your question. Please try again.',
            });
          }
        } else {
          this.setState('chatMessages', formattedMessages);
        }
      }
      this.listenChatMessageEvents(chatId);
      this.listenChatMessageErrorEvents(chatId);
      setTimeout(() => this.scrollToBottom());
    } else {
      this.unsub(this.SUB_ID_LISTEN_CHAT_MESSAGE_EVENTS);
      this.unsub(this.SUB_ID_LISTEN_CHAT_MESSAGE_ERROR_EVENTS);
      this.unsub(this.SUB_ID_FETCH_REPLACE_MESSAGE_PAIR);
      this.setState('chatId', undefined as State['chatId']);
      this.setState('chatMessages', undefined as State['chatMessages']);
    }
  };

  private isRecentMessage = (message: ChatMessage) =>
    differenceInMinutes(new Date(), new Date(message.createdAt)) <= MESSAGE_TIMEOUT_MINUTES;

  private formatChatMessage = (message: ChatMessage): ChatMessage => {
    return {
      ...message,
      isAiResponse:
        'isAiResponse' in message && message.isAiResponse !== undefined
          ? message.isAiResponse
          : !message.user,
    };
  };

  private formatChatMessages = (messages: ChatMessage[] | null): State['chatMessages'] => {
    if (!messages) return undefined as State['chatMessages'];
    return messages.map((message) => this.formatChatMessage(message)) as State['chatMessages'];
  };

  public saveFeedback = (data: {
    messageId: ChatMessage['id'];
    feedback: boolean;
    comments?: string;
    successMessage?: string;
    errorMessage?: string;
  }) => {
    const { messageId, feedback, comments, successMessage, errorMessage } = data;
    if (this.saveMessageFeedbackEndpoint) {
      this.mutateState('chatMessages', (messages) =>
        (messages?.slice() || []).map((m: ChatMessage) =>
          m.id === messageId ? { ...m, feedbackPositive: feedback } : m
        )
      );
      this.addSub(
        $post(
          this.saveMessageFeedbackEndpoint(messageId),
          {
            feedbackPositive: feedback,
            ...(comments && { comments }),
          },
          {
            useSnakeCase: true,
          }
        ).subscribe({
          next: () => {
            if (successMessage) snackbarV2Success({ title: successMessage });
          },
          error: () => {
            this.mutateState('chatMessages', (messages) =>
              (messages?.slice() || []).map((m: ChatMessage) =>
                m.id === messageId ? { ...m, feedbackPositive: null } : m
              )
            );
            snackbarV2Error({
              title: errorMessage || 'We cannot send your feedback right now. Try again later.',
            });
          },
        }),
        'saveFeedback'
      );
    }
  };

  protected onChatMessageEventReceived = () => {};

  protected listenChatMessageEvents = (chatId: ChatMessage['chatId']) => {
    this.addSub(
      this.pusher
        .$listen<{ chatId: ChatMessage['chatId']; messageSentId: ChatMessage['id'] }>('.ChatMessageReceived')
        .pipe(filter((event) => event.chatId === chatId))
        .subscribe(() => this.onChatMessageEventReceived()),
      this.SUB_ID_LISTEN_CHAT_MESSAGE_EVENTS
    );
  };

  protected onChatMessageErrorEventReceived = () => {};

  protected listenChatMessageErrorEvents = (chatId: ChatMessage['chatId']) => {
    this.addSub(
      this.pusher
        .$listen<{ chatId: ChatMessage['chatId'] }>('.ChatMessageLost')
        .pipe(filter((event) => event.chatId === chatId))
        .subscribe(() => {
          snackbarV2Error({
            title: 'We cannot send your message right now. Try again later.',
          });
          this.onChatMessageErrorEventReceived();
        }),
      this.SUB_ID_LISTEN_CHAT_MESSAGE_ERROR_EVENTS
    );
  };

  protected fetchAndReplaceUserAgentMessagesPair = ({
    id,
    params = {},
  }: {
    id: number;
    params?: Options['searchParams'];
  }) => {
    if (this.getChatMessagesEndpoint) {
      this.addSub(
        $get<ChatMessage[]>(
          this.getChatMessagesEndpoint(id),
          {
            ...params,
            page: 1,
            perPage: 2,
          },
          { useSnakeCase: true }
        ).subscribe({
          next: (msgs) => {
            this.mutateState('chatMessages', (messages) =>
              msgs[0].id !== messages?.[0]?.id
                ? messages.toSpliced(
                    0,
                    2,
                    ...[
                      { ...this.formatChatMessage(msgs[0]), animated: true },
                      this.formatChatMessage(msgs[1]),
                    ]
                  )
                : messages
            );
          },
          error: () => {
            snackbarV2Error({
              title: 'We could not retrieve your latest messages. Please try again later.',
            });
          },
        }),
        this.SUB_ID_FETCH_REPLACE_MESSAGE_PAIR
      );
    }
  };

  protected addUserAgentMessagesPair = (
    message: BLoCWithChatBaseFormMessage['message'],
    options?: {
      adPersona?: ChatMessage['adPersona'];
      images?: ChatMessage['images'];
    }
  ) => {
    const { adPersona, images } = options || {};
    this.mutateState('chatMessages', (messages) => {
      const newMessages = [
        {
          ...getChatMessage({ message: 'Thinking', awaiting: true, isAiResponse: true }),
          ...(adPersona && { adPersona }),
        },
        {
          ...getChatMessage({ message, awaiting: true, isAiResponse: false }),
          ...(adPersona && { adPersona }),
          ...(images?.length && { images }),
        },
        ...(messages || []),
      ] as State['chatMessages'];
      return newMessages;
    });
    this.scrollToBottom();
  };

  protected removeUserAgentMessagesPair = (index?: number) => {
    this.mutateState('chatMessages', (messages) => {
      return messages?.slice(index || 2);
    });
  };

  protected sendMessageSuccess = () => {};

  protected sendMessageError = () => {};

  protected _sendMessage = (
    id: number,
    message: BLoCWithChatBaseFormMessage['message'],
    options?: {
      params?: Options['searchParams'];
      adPersona?: ChatMessage['adPersona'];
      images?: ChatMessage['images'];
    }
  ) => {
    const { params, adPersona, images } = options || {};
    if (this.sendMessageEndpoint) {
      this.addUserAgentMessagesPair(message, {
        adPersona,
        images,
      });
      this.resetMessageForm();
      this.addSub(
        $post<ChatMessage>(
          this.sendMessageEndpoint(id),
          {
            message,
            ...params,
            ...(adPersona && { adPersonaId: adPersona.id }),
            ...(images?.length && { imageIds: images.map((image) => image.id) }),
          },
          { useSnakeCase: true }
        ).subscribe({
          next: () => this.sendMessageSuccess(),
          error: () => {
            snackbarV2Error({
              title: 'We cannot send your message right now. Try again later.',
            });
            this.sendMessageError();
          },
        }),
        this.SUB_ID_SEND_MESSAGE
      );
    }
  };
}

/**
 * Create a new subject based on a pre-defined type.
 * Exposes two methods:
 * - subscribe: to subscribe passing an object with observers.
 * - notify: to fire an event name based on the pre-defined events.
 */
export const createSubjectableBLoC = <T extends { [key: string]: Record<string, any> | null }>() => {
  const $subject = new Subject<{ type: keyof T; payload: T[keyof T] }>();
  return {
    subscribe: (observers: Partial<{ [K in keyof T]: (payload: T[K]) => void }>) =>
      $subject.subscribe((ev) => {
        observers[ev.type]?.(ev.payload);
      }),
    notify: <E extends keyof T>(event: E, payload: T[E]) => {
      $subject.next({ type: event, payload });
    },
    $subject,
  };
};

export const BLoCWithChatBaseBLoCSubject = createSubjectableBLoC<{
  scrollToBottom: {};
}>();
